import React, { useState } from 'react';
import { Download, Plus, Trash2, Eye, EyeOff, Sparkles} from 'lucide-react';
import { downloadQuickBooksCSV } from '../utils/QuickBooksExport';
const DirectEditField = ({ label, value, onChange, type = 'text', required = false, placeholder = '' }) => {
  const inputRef = React.useRef(null);
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        ref={inputRef}
        type={type}
        defaultValue={value || ''}
        onBlur={(e) => onChange(e.target.value)}
        onChange={(e) => {
          inputRef.current.value = e.target.value;
        }}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
      />
    </div>
  );
};

const QuickBooksEditor = ({ invoiceData, setInvoiceData, pdfFile, showJson, setShowJson }) => {
  const [expandedLines, setExpandedLines] = useState({});

  const updateField = (path, value) => {
    const newData = JSON.parse(JSON.stringify(invoiceData));
    const keys = path.split('.');
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setInvoiceData(newData);
  };

  const addInvoiceLine = () => {
    const newLine = {
      'line-num': String(invoiceData['invoice-lines'].length + 1),
      description: '',
      'item-type': 'Service',
      type: 'InvoiceQuantityLine',
      currency: { code: 'USD' },
      price: '',
      quantity: '',
      uom: { code: '' },
      'po-number': '',
      'order-line-num': '',
      'supplier-part-number': '',
      'auxiliary-part-number': '',
      account: {
        code: '',
        'account-type': { name: 'Chart of Accounts' }
      },
      'custom-fields': { 'invoice-line-total': '' },
      'tax-lines': []
    };
    setInvoiceData({
      ...invoiceData,
      'invoice-lines': [...invoiceData['invoice-lines'], newLine]
    });
  };

  const updateInvoiceLine = (index, field, value) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    
    if (!newLines[index].account) {
      newLines[index].account = {
        code: '',
        'account-type': { name: '' }
      };
    }
    
    const keys = field.split('.');
    let current = newLines[index];
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const removeLine = (index) => {
    const newLines = invoiceData['invoice-lines'].filter((_, i) => i !== index);
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const addTaxLine = (lineIndex) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    if (!newLines[lineIndex]['tax-lines']) {
      newLines[lineIndex]['tax-lines'] = [];
    }
    newLines[lineIndex]['tax-lines'].push({
      amount: '',
      type: 'TaxLine',
      code: '',
      rate: '',
      base: ''
    });
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const updateLineTax = (lineIndex, taxIndex, field, value) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    newLines[lineIndex]['tax-lines'][taxIndex][field] = value;
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const removeLineTax = (lineIndex, taxIndex) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    newLines[lineIndex]['tax-lines'].splice(taxIndex, 1);
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const addHeaderTax = () => {
    setInvoiceData({
      ...invoiceData,
      'tax-lines': [...(invoiceData['tax-lines'] || []), { amount: '', rate: '', base: '', description: '' }]
    });
  };

  const removeHeaderTax = (taxIndex) => {
    const updatedTaxes = invoiceData['tax-lines'].filter((_, i) => i !== taxIndex);
    setInvoiceData({ ...invoiceData, 'tax-lines': updatedTaxes });
  };

  const updateHeaderTax = (taxIndex, field, value) => {
    const updatedTaxes = [...invoiceData['tax-lines']];
    updatedTaxes[taxIndex] = { ...updatedTaxes[taxIndex], [field]: value };
    setInvoiceData({ ...invoiceData, 'tax-lines': updatedTaxes });
  };

  const toggleAllLines = () => {
    const allExpanded = Object.keys(expandedLines).length === invoiceData['invoice-lines'].length && 
                        Object.values(expandedLines).every(v => v);
    
    if (allExpanded) {
      setExpandedLines({});
    } else {
      const newExpanded = {};
      invoiceData['invoice-lines'].forEach((_, index) => {
        newExpanded[index] = true;
      });
      setExpandedLines(newExpanded);
    }
  };

  const toggleLine = (index) => {
    setExpandedLines({
      ...expandedLines,
      [index]: !expandedLines[index]
    });
  };

  return (
    <div className="space-y-4 h-[calc(100vh-120px)] overflow-y-auto pr-2">
      {/* Invoice Header */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-indigo-600 rounded"></div>
          Invoice Header
        </h4>
        <div className="grid grid-cols-2 gap-x-4">
          <DirectEditField label="Invoice Number" value={invoiceData['invoice-number']} onChange={(v) => updateField('invoice-number', v)} required placeholder="INV-12345" />
          <DirectEditField label="Invoice Date" value={invoiceData['invoice-date']} onChange={(v) => updateField('invoice-date', v)} required placeholder="MM/DD/YYYY" />
          <DirectEditField label="Due Date" value={invoiceData['due-date']} onChange={(v) => updateField('due-date', v)} placeholder="MM/DD/YYYY" />
          <DirectEditField label="Currency Code" value={invoiceData.currency.code} onChange={(v) => updateField('currency.code', v)} required placeholder="USD" />
          <DirectEditField label="Payment Terms" value={invoiceData['payment-terms']} onChange={(v) => updateField('payment-terms', v)} placeholder="Net 30" />
          <DirectEditField label="Requester Email" value={invoiceData['requester-email']} onChange={(v) => updateField('requester-email', v)} type="email" placeholder="user@company.com" />
        </div>
      </div>

      {/* Customer Information (QuickBooks uses customer, not supplier) */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-green-600 rounded"></div>
          Customer Information (Buyer)
        </h4>
        <div className="grid grid-cols-2 gap-x-4">
          <DirectEditField label="Customer Name" value={invoiceData.customer.name} onChange={(v) => updateField('customer.name', v)} required placeholder="ABC Company" />
          <DirectEditField label="Customer Email" value={invoiceData.customer.email} onChange={(v) => updateField('customer.email', v)} type="email" placeholder="customer@company.com" />
          <div className="col-span-2">
            <DirectEditField label="Billing Address" value={invoiceData.customer.address} onChange={(v) => updateField('customer.address', v)} placeholder="123 Main St" />
          </div>
          <DirectEditField label="City" value={invoiceData.customer.city} onChange={(v) => updateField('customer.city', v)} placeholder="New York" />
          <DirectEditField label="State" value={invoiceData.customer.state} onChange={(v) => updateField('customer.state', v)} placeholder="NY" />
          <DirectEditField label="ZIP Code" value={invoiceData.customer.zip} onChange={(v) => updateField('customer.zip', v)} placeholder="10001" />
          <DirectEditField label="Country" value={invoiceData.customer.country} onChange={(v) => updateField('customer.country', v)} placeholder="USA" />
        </div>
      </div>

      {/* Invoice Charges */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-cyan-600 rounded"></div>
          Invoice Charges
        </h4>
        <div className="grid grid-cols-2 gap-x-4">
          <DirectEditField 
            label="Shipping Amount" 
            value={invoiceData['shipping-amount']} 
            onChange={(v) => updateField('shipping-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
          <DirectEditField 
            label="Handling Amount" 
            value={invoiceData['handling-amount']} 
            onChange={(v) => updateField('handling-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
          <DirectEditField 
            label="Miscellaneous Amount" 
            value={invoiceData['misc-amount']} 
            onChange={(v) => updateField('misc-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
          <DirectEditField 
            label="Discount Amount" 
            value={invoiceData['discount-amount']} 
            onChange={(v) => updateField('discount-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
        </div>
      </div>

      {/* Invoice Line Items */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <div className="w-1 h-5 bg-pink-600 rounded"></div>
            Invoice Line Items ({invoiceData['invoice-lines'].length})
          </h4>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAllLines}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              {Object.keys(expandedLines).length === invoiceData['invoice-lines'].length && 
               Object.values(expandedLines).every(v => v) ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Collapse All
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Expand All
                </>
              )}
            </button>
            <button
              onClick={addInvoiceLine}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Line
            </button>
          </div>
        </div>

        {invoiceData['invoice-lines'].map((line, index) => (
          <div key={index} className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleLine(index)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  {expandedLines[index] ? (
                    <EyeOff className="w-4 h-4 text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                <span className="text-sm font-semibold text-indigo-600">Line {line['line-num']}</span>
                <span className="text-xs text-gray-500">
                  {line.description ? `- ${line.description.substring(0, 30)}${line.description.length > 30 ? '...' : ''}` : ''}
                </span>
              </div>
              <button onClick={() => removeLine(index)} className="p-1 text-red-600 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {expandedLines[index] && (
              <>
                <div className="grid grid-cols-2 gap-x-3">
                  <div className="col-span-2">
                    <DirectEditField label="Description" value={line.description} onChange={(v) => updateInvoiceLine(index, 'description', v)} placeholder="Item description" />
                  </div>
                  
                  {/* QuickBooks Item Type */}
                  <div className="col-span-2 mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      QuickBooks Item Type <span className="text-blue-600 text-xs">(AI Suggested)</span>
                    </label>
                    <input
                      type="text"
                      value={line['item-type'] || 'Service'}
                      onChange={(e) => updateInvoiceLine(index, 'item-type', e.target.value)}
                      placeholder="e.g., Service, Inventory, Non-Inventory"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    />
                    <div className="flex items-start gap-1 mt-1">
                      <Sparkles className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-500">
                        AI suggested: <span className="font-semibold text-blue-600">{line['item-type'] || 'Service'}</span> - Common types: Service, Non-Inventory, Inventory
                      </p>
                    </div>
                  </div>

                  <DirectEditField label="Quantity" value={line.quantity} onChange={(v) => updateInvoiceLine(index, 'quantity', v)} type="number" placeholder="1" />
                  <DirectEditField label="Unit Price" value={line.price} onChange={(v) => updateInvoiceLine(index, 'price', v)} type="number" placeholder="100.00" />
                  <DirectEditField label="Line Total" value={line['custom-fields']['invoice-line-total']} onChange={(v) => updateInvoiceLine(index, 'custom-fields.invoice-line-total', v)} type="number" placeholder="100.00" />
                </div>

                {/* Tax Lines */}
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-blue-800 font-medium">Tax Lines</p>
                    <button
                      onClick={() => addTaxLine(index)}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      + Add Tax
                    </button>
                  </div>
                  {line['tax-lines'] && line['tax-lines'].map((tax, taxIndex) => (
                    <div key={taxIndex} className="mb-2 p-2 bg-white rounded border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-blue-700">Tax {taxIndex + 1}</span>
                        <button
                          onClick={() => removeLineTax(index, taxIndex)}
                          className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <DirectEditField label="Tax Amount" value={tax.amount} onChange={(v) => updateLineTax(index, taxIndex, 'amount', v)} type="number" placeholder="10.00" />
                        <DirectEditField label="Tax Rate %" value={tax.rate} onChange={(v) => updateLineTax(index, taxIndex, 'rate', v)} type="number" placeholder="8.5" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Header Level Taxes */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Header Taxes</h3>
          <button
            onClick={addHeaderTax}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            + Add Header Tax
          </button>
        </div>
        
        {invoiceData['tax-lines'] && invoiceData['tax-lines'].map((tax, taxIndex) => (
          <div key={taxIndex} className="mb-2 p-2 bg-white rounded border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-blue-700">Tax {taxIndex + 1}</span>
              <button
                onClick={() => removeHeaderTax(taxIndex)}
                className="p-0.5 text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <DirectEditField 
                label="Tax Amount" 
                value={tax.amount} 
                onChange={(v) => updateHeaderTax(taxIndex, 'amount', v)} 
                type="number" 
                placeholder="10.00" 
              />
              <DirectEditField 
                label="Tax Rate %" 
                value={tax.rate} 
                onChange={(v) => updateHeaderTax(taxIndex, 'rate', v)} 
                type="number" 
                placeholder="8.5" 
              />
              <div className="col-span-2">
                <DirectEditField 
                  label="Description" 
                  value={tax.description} 
                  onChange={(v) => updateHeaderTax(taxIndex, 'description', v)} 
                  placeholder="Sales Tax" 
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Invoice Summary */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-green-600 rounded"></div>
          Invoice Summary
        </h4>
        <div className="space-y-3">
          <DirectEditField label="Invoice Total" value={invoiceData['custom-fields']['invoice-total']} onChange={(v) => updateField('custom-fields.invoice-total', v)} type="number" placeholder="1000.00" />
          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
            <span className="text-2xl font-bold text-indigo-600">
              {invoiceData.currency.code} {invoiceData['custom-fields']['invoice-total']}
            </span>
          </div>
        </div>
      </div>

      {/* JSON Preview */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-orange-600 rounded"></div>
          JSON Preview (View Only)
        </h4>
        <button
          onClick={() => setShowJson(!showJson)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium mb-3"
        >
          {showJson ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showJson ? 'Hide' : 'Show'} JSON Structure
        </button>
        {showJson && (
          <div className="relative">
            <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-red-500 text-white text-xs rounded font-medium">
              READ ONLY
            </div>
            <pre
              className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-[500px]"
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
            >
              {JSON.stringify(invoiceData, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-indigo-600 rounded"></div>
          Export QuickBooks CSV
        </h4>
        
        <button
          onClick={() => downloadQuickBooksCSV(invoiceData, pdfFile)}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all"
        >
          <Download className="w-5 h-5" />
          Download QuickBooks CSV
        </button>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-semibold text-blue-900 mb-1">
            📊 QuickBooks Online Import
          </p>
          <p className="text-xs text-blue-800">
            CSV format is recommended for QuickBooks. Go to Sales → Invoices → Import to upload your file.
          </p>
        </div>

        <p className="text-xs text-gray-500 text-center mt-3">
          ✅ No payment required - Download freely after processing
        </p>
      </div>
    </div>
  );
};

export default QuickBooksEditor;